import { z } from 'zod';
import { EntitySchema } from './shared.schema';

/**
 * Role of the character in the story.
 */
export const CharacterTypeEnum = z.enum(['Protagonist', 'Antagonist', 'Supporting', 'Minor']);

/**
 * Character voice profile schema.
 */
export const VoiceProfileSchema = z.object({
  archetype: z.string().describe('The voice archetype of the character.'),
  baseStyle: z.object({
    tone: z.enum(['formal', 'casual', 'poetic', 'brutal', 'sarcastic', 'humble', 'authoritative', 'childish', 'melancholic']).describe('The primary tone of the character’s voice.'),
    directness: z.enum(['direct', 'indirect']).describe('How direct should the character be.'),
    verbosity: z.enum(['low', 'medium', 'high']).describe('How much the character speaks.'),
    emotionalExpression: z.enum(['contained', 'balanced', 'expressive', 'volatile']).describe('How much emotion the character shows.'),
  }),
  verbalTics: z.array(z.string()).describe('List of common expressions or verbal tics.'),
});

/**
 * Character schema.
 * Master source for Character entity structure.
 */
export const CharacterSchema = EntitySchema.extend({
  type: CharacterTypeEnum.describe('The role of the character in the story.'),
  fullName: z.string().describe('Full Name'),
  nickname: z.string().describe('Nickname / Alias'),
  characterClass: z.string().describe('Class / Essence'),
  organizationIds: z.array(z.string()).describe('IDs of organizations the character belongs to').optional(),
  voiceProfile: VoiceProfileSchema.optional().describe('Voice configuration for the character.'),
});

export type Character = z.infer<typeof CharacterSchema>;
export type VoiceProfile = z.infer<typeof VoiceProfileSchema>;

/**
 * Character voice archetypes.
 */
export const VOICE_ARCHETYPES = [
  {
    key: "stoic_warrior",
    name: "Stoic Warrior",
    baseStyle: { tone: "formal", directness: "direct", verbosity: "low", emotionalExpression: "contained" }
  },
  {
    key: "noble_knight",
    name: "Noble Knight",
    baseStyle: { tone: "formal", directness: "direct", verbosity: "medium", emotionalExpression: "balanced" }
  },
  {
    key: "poetic_rogue",
    name: "Poetic Rogue",
    baseStyle: { tone: "poetic", directness: "indirect", verbosity: "high", emotionalExpression: "expressive" }
  },
  {
    key: "cynical_mercenary",
    name: "Cynical Mercenary",
    baseStyle: { tone: "casual", directness: "direct", verbosity: "medium", emotionalExpression: "contained" }
  },
  {
    key: "idealistic_mage",
    name: "Idealistic Mage",
    baseStyle: { tone: "formal", directness: "indirect", verbosity: "high", emotionalExpression: "expressive" }
  },
  {
    key: "broken_survivor",
    name: "Broken Survivor",
    baseStyle: { tone: "casual", directness: "indirect", verbosity: "low", emotionalExpression: "volatile" }
  },
  {
    key: "hotheaded_fighter",
    name: "Hotheaded Fighter",
    baseStyle: { tone: "casual", directness: "direct", verbosity: "medium", emotionalExpression: "volatile" }
  }
] as const;

